import React, { useState, useCallback, useEffect, useRef } from 'react';
import api from '../services/api';
import { isVisualizacao } from '../utils/permissoes';
import { useAuth } from '../context/AuthContext';

const STATUS = [
  { v: 'novo', l: 'Novo', cor: '#1A3A5C', bg: '#E6F1FB' },
  { v: 'contato', l: 'Em contato', cor: '#2471A3', bg: '#D6EAF8' },
  { v: 'simulando', l: 'Simulando', cor: '#6B3FA0', bg: '#F3E8FF' },
  { v: 'aprovado', l: 'Aprovado financ.', cor: '#117A65', bg: '#D1F2EB' },
  { v: 'vem_loja', l: 'Vem na loja', cor: '#E67E22', bg: '#FEF5E7' },
  { v: 'fechado', l: 'Fechado', cor: '#2D7D4F', bg: '#EAF5EE' },
  { v: 'perdido', l: 'Perdido', cor: '#C0392B', bg: '#FDECEA' },
];

const fmt = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};
const isVencido = (d) => d && new Date(d) <= new Date();
const isHoje = (d) => {
  if (!d) return false;
  const dt = new Date(d); const hoje = new Date();
  return dt.toDateString() === hoje.toDateString();
};

// Classificação de urgência do follow-up: 0=vencido, 1=urgente (vence em <=48h), 2=normal (futuro distante ou sem data)
const FU = { VENCIDO: 0, URGENTE: 1, NORMAL: 2 };
const URGENTE_MS = 48 * 60 * 60 * 1000; // janela de 48h para "a vencer"
const classFollowup = (d) => {
  if (!d) return FU.NORMAL;                 // sem data definida -> normal (fim da lista)
  const diff = new Date(d).getTime() - Date.now();
  if (isNaN(diff)) return FU.NORMAL;
  if (diff <= 0) return FU.VENCIDO;          // data de retorno já passou
  if (diff <= URGENTE_MS) return FU.URGENTE; // vence hoje ou nas próximas 24-48h
  return FU.NORMAL;
};
// Ordena dentro da coluna: vencido > urgente > normal; na mesma faixa, data mais próxima primeiro; sem data por último
const ordenarPorFollowup = (a, b) => {
  const ca = classFollowup(a.proximo_followup), cb = classFollowup(b.proximo_followup);
  if (ca !== cb) return ca - cb;
  const ta = a.proximo_followup ? new Date(a.proximo_followup).getTime() : Infinity;
  const tb = b.proximo_followup ? new Date(b.proximo_followup).getTime() : Infinity;
  return ta - tb;
};
// Cor do badge de prazo conforme a urgência do follow-up
const badgeFollowupStyle = (d) => {
  const c = classFollowup(d);
  if (c === FU.VENCIDO) return { background: '#C0392B', color: '#fff' };   // vermelho destacado
  if (c === FU.URGENTE) return { background: '#FEF5E7', color: '#E67E22' }; // laranja
  return { background: '#F4F6F9', color: '#9AA3AE' };                       // neutro
};
// Destaque do CARD INTEIRO conforme a urgência: borda lateral grossa + fundo tingido bem sutil (não atrapalha a leitura do texto preto)
const cardFollowupStyle = (d) => {
  const c = classFollowup(d);
  if (c === FU.VENCIDO) return { borderLeft: '4px solid #C0392B', background: '#FDF0EF' }; // vermelho
  if (c === FU.URGENTE) return { borderLeft: '4px solid #E67E22', background: '#FFF8F0' }; // laranja
  return {};                                                                                // normal: branco/neutro atual
};

const statusCfg = (v) => STATUS.find(s => s.v === v) || STATUS[0];

// Origens conhecidas -> ícone + label (paleta/sistema já existente)
const ORIGENS = {
  internet: { icon: '🌐', label: 'Internet' },
  site:     { icon: '🌐', label: 'Site' },
  whatsapp: { icon: '💬', label: 'WhatsApp' },
  instagram:{ icon: '📷', label: 'Instagram' },
  facebook: { icon: '📘', label: 'Facebook' },
  meta:     { icon: '📣', label: 'Anúncio' },
  anuncio:  { icon: '📣', label: 'Anúncio' },
  indicacao:{ icon: '🤝', label: 'Indicação' },
  manual:   { icon: '✍️', label: 'Manual' },
};
const origemCfg = (o) => ORIGENS[(o || 'manual').toLowerCase()] || { icon: '📍', label: o || 'Outro' };

// "X dias na etapa" — aproximado pela última atualização (updated_at) ou criação
const diasNaEtapa = (l) => {
  const base = l.updated_at || l.created_at;
  if (!base) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(base).getTime()) / 86400000));
};
const labelDias = (n) => n == null ? '—' : n <= 0 ? 'Hoje' : n === 1 ? '1 dia' : n + ' dias';
// Dias (calendário) até o próximo follow-up. Bônus: null se ausente, inválida ou NÃO futura.
const inicioDoDia = (t) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };
const diasAteFollowup = (d) => {
  if (!d) return null;
  const t = new Date(d).getTime();
  if (isNaN(t) || t <= Date.now()) return null;   // sem data, inválida ou já passou
  return Math.round((inicioDoDia(t) - inicioDoDia(Date.now())) / 86400000);
};
const labelFaltam = (n) => n <= 0 ? 'Follow-up hoje' : n === 1 ? 'Falta 1 dia' : `Faltam ${n} dias`;
const labelCriado = (n) => n == null ? null : n <= 0 ? 'Criado hoje' : `Criado há ${n}`;
const inicial = (nome) => (nome || '?').trim().charAt(0).toUpperCase();
const periodoLabel = () => {
  const fim = new Date(); const ini = new Date(); ini.setDate(ini.getDate() - 30);
  const f = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${f(ini)} - ${f(fim)}`;
};

const card = { background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px' };
const iconBtn = { width: '24px', height: '24px', border: 'none', background: 'transparent', color: '#9AA3AE', cursor: 'pointer', fontSize: '13px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 };
const inpStyle = { width: '100%', padding: '8px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' };
const lbl = { color: '#6B7A8D', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' };

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');
  const [colapsadas, setColapsadas] = useState({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [modalLead, setModalLead] = useState(false);
  const [modalDetalhe, setModalDetalhe] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [form, setForm] = useState({});
  const [dupAviso, setDupAviso] = useState(null); // lead existente c/ mesmo telefone (409)
  const [formHist, setFormHist] = useState({ descricao: '', tipo: 'observacao', proximo_followup: '' });
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState({});      // { status_key: label_customizado } da empresa
  const [editandoCol, setEditandoCol] = useState(null); // status_key da coluna em edição
  const cancelarEdicaoRef = useRef(false);              // marca quando o blur veio de um Esc (cancelar)
  const alertIntervalRef = useRef(null);

  const { usuario } = useAuth();
  const isAdmin = usuario?.role === 'admin' || usuario?.nivel === 'admin';
  const somenteLeitura = isVisualizacao(usuario); // só vê: sem criar/editar/mover lead

  // STATUS com rótulos mesclados: usa o customizado da empresa quando houver, senão o padrão.
  const STATUS_M = STATUS.map(s => ({ ...s, l: labels[s.v] || s.l }));
  const cfgM = (v) => STATUS_M.find(s => s.v === v) || STATUS_M[0];

  const tocarAlerta = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880; g.gain.setValueAtTime(0.3, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.5);
    } catch(e) {}
  }, []);

  const carregarLeads = useCallback(async () => {
    const r = await api.get('/crm/leads', { params: { status: filtroStatus || undefined } });
    setLeads(r.data);
  }, [filtroStatus]);

  const carregarAlertas = useCallback(async () => {
    const r = await api.get('/crm/leads/alertas');
    setAlertas(r.data);
    if (r.data.length > 0) tocarAlerta();
  }, [tocarAlerta]);

  const carregarLabels = useCallback(async () => {
    try {
      const r = await api.get('/crm/status-labels');
      setLabels(r.data || {});
    } catch (e) { /* mantém padrões em caso de falha */ }
  }, []);

  // Salva (ou limpa, se vazio/igual ao padrão) o rótulo de uma coluna.
  // Otimista: atualiza local e reverte se a API falhar. Recebe o valor digitado (input não-controlado).
  const salvarLabel = async (statusKey, valor) => {
    const novo = (valor || '').trim();
    const padrao = STATUS.find(s => s.v === statusKey)?.l;
    const anterior = labels;
    setEditandoCol(null);
    const ehPadrao = !novo || novo === padrao;
    setLabels(prev => {
      const next = { ...prev };
      if (ehPadrao) delete next[statusKey];
      else next[statusKey] = novo;
      return next;
    });
    try {
      await api.put('/crm/status-labels', { status_key: statusKey, label: ehPadrao ? '' : novo });
    } catch (e) {
      setLabels(anterior);
      alert(e.response?.data?.error || 'Não foi possível salvar o nome da coluna.');
    }
  };

  useEffect(() => {
    carregarLeads(); carregarAlertas(); carregarLabels();
    alertIntervalRef.current = setInterval(carregarAlertas, 60000);
    return () => clearInterval(alertIntervalRef.current);
  }, [carregarLeads, carregarAlertas, carregarLabels]);

  useEffect(() => { carregarLeads(); }, [filtroStatus, carregarLeads]);

  const salvarLead = async (force = false) => {
    if (!form.nome) { alert('Informe o nome'); return; }
    setLoading(true);
    try {
      if (form.id) await api.put('/crm/leads/' + form.id, form);
      else {
        const r = await api.post('/crm/leads', force ? { ...form, force: true } : form);
        if (form.observacao) {
          await api.post('/crm/leads/' + r.data.id + '/historico', { descricao: form.observacao, tipo: 'observacao' });
        }
      }
      setModalLead(false); setForm({}); setDupAviso(null);
      carregarLeads(); carregarAlertas();
    } catch(e) {
      // Telefone duplicado (Camada 2): não é erro genérico — mostra o cadastro existente.
      if (e.response?.status === 409 && e.response.data?.error === 'telefone_duplicado') {
        setDupAviso(e.response.data.existente);
      } else {
        alert(e.response?.data?.error || 'Erro');
      }
    }
    finally { setLoading(false); }
  };

  // "Ver cadastro" do aviso de duplicidade: abre o detalhe do lead existente
  // (se estiver na lista carregada) ou joga o nome na busca.
  const verCadastroExistente = () => {
    const alvo = dupAviso; if (!alvo) return;
    setDupAviso(null); setModalLead(false); setForm({});
    const existLead = leads.find(l => l.id === alvo.id);
    if (existLead) abrirDetalhe(existLead);
    else setBusca(alvo.nome || '');
  };

  const abrirDetalhe = async (lead) => {
    setModalDetalhe(lead);
    const r = await api.get('/crm/leads/' + lead.id + '/historico');
    setHistorico(r.data);
    setFormHist({ descricao: '', tipo: 'observacao', proximo_followup: '' });
  };

  const salvarHistorico = async () => {
    if (!formHist.descricao) { alert('Informe a observação'); return; }
    try {
      await api.post('/crm/leads/' + modalDetalhe.id + '/historico', formHist);
      if (formHist.status) {
        await api.put('/crm/leads/' + modalDetalhe.id, { ...modalDetalhe, status: formHist.status, proximo_followup: formHist.proximo_followup || modalDetalhe.proximo_followup });
      }
      const r = await api.get('/crm/leads/' + modalDetalhe.id + '/historico');
      setHistorico(r.data);
      setFormHist({ descricao: '', tipo: 'observacao', proximo_followup: '' });
      carregarLeads(); carregarAlertas();
    } catch(e) { alert('Erro'); }
  };

  const abrirWhatsApp = (tel) => {
    const num = tel.replace(/\D/g, '');
    window.open('https://wa.me/55' + num, '_blank');
  };

  const matchBusca = (l) => {
    if (!busca) return true;
    const b = busca.toLowerCase();
    return (l.nome || '').toLowerCase().includes(b) || (l.telefone || '').includes(b)
      || (l.interesse || '').toLowerCase().includes(b) || (l.ultima_obs || '').toLowerCase().includes(b);
  };
  const porStatus = (v) => leads.filter(l => (l.status || 'novo') === v && matchBusca(l)).sort(ordenarPorFollowup);
  const toggleCol = (v) => setColapsadas(c => ({ ...c, [v]: !c[v] }));

  // Move um lead para outra etapa via drag-and-drop (atualização otimista + revert em caso de erro)
  const moverLead = async (id, novoStatus) => {
    const lead = leads.find(l => l.id === id);
    if (!lead || lead.status === novoStatus) return;
    const statusAnterior = lead.status;
    // otimista: muda de coluna na hora, sem esperar o servidor
    setLeads(ls => ls.map(l => l.id === id ? { ...l, status: novoStatus, updated_at: new Date().toISOString() } : l));
    try {
      await api.put('/crm/leads/' + id, { ...lead, status: novoStatus });
      carregarAlertas();
    } catch (e) {
      // reverte para a etapa anterior se a chamada falhar
      setLeads(ls => ls.map(l => l.id === id ? { ...l, status: statusAnterior } : l));
      alert('Não foi possível mover o lead. Tente novamente.');
    }
  };

  const colunasVisiveis = STATUS_M.filter(s => !filtroStatus || s.v === filtroStatus);
  const totalVisivel = leads.filter(matchBusca).length;

  const CardLead = ({ l }) => {
    const sc = statusCfg(l.status);
    const vencido = isVencido(l.proximo_followup);
    const hoje = isHoje(l.proximo_followup);
    const dias = diasNaEtapa(l);
    const org = origemCfg(l.origem);
    const perdido = l.status === 'perdido';
    const fechado = l.status === 'fechado';
    // "Faltam X dias" só faz sentido p/ lead ativo com follow-up futuro válido (null caso contrário).
    const ate = (!perdido && !fechado) ? diasAteFollowup(l.proximo_followup) : null;
    // Destaque do card inteiro só faz sentido p/ leads ativos (vencido/urgente não se aplica a venda fechada/perdida)
    const destaque = (!perdido && !fechado) ? cardFollowupStyle(l.proximo_followup) : {};
    return (
      <div
        draggable={!somenteLeitura}
        onDragStart={e => { setDragId(l.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(l.id)); } catch (_) {} }}
        onDragEnd={() => { setDragId(null); setDragOverCol(null); }}
        style={{ ...card, ...destaque, padding: '12px 14px', marginBottom: '10px', cursor: dragId === l.id ? 'grabbing' : 'pointer', position: 'relative', opacity: dragId === l.id ? 0.4 : 1, transition: 'opacity 0.12s' }}
        onClick={() => abrirDetalhe(l)}>
        {perdido ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#FEF5E7', color: '#B06000', border: '0.5px solid #F0C089', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', marginBottom: '8px' }}>⚠ Venda não Realizada</div>
        ) : fechado ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#EAF5EE', color: '#2D7D4F', border: '0.5px solid #A9D8BB', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', marginBottom: '8px' }}>✓ Venda Realizada</div>
        ) : (vencido || hoje) ? (
          <div style={{ fontSize: '13px', marginBottom: '2px', color: vencido ? '#C0392B' : '#E67E22' }}>{vencido ? '⏰' : '🔔'}</div>
        ) : null}

        <div style={{ fontSize: '14px', fontWeight: '700', color: '#1C2533', marginBottom: '4px' }}>{l.nome}</div>

        {l.ultima_obs && (
          <div style={{ fontSize: '12px', color: '#6B7A8D', marginBottom: '8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.35' }}>{l.ultima_obs}</div>
        )}

        {l.interesse && (
          <div style={{ display: 'block', background: '#F0F2F5', color: '#5A6B7D', padding: '3px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', marginBottom: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.interesse}</div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginTop: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            {ate != null ? (
              // Follow-up futuro válido: destaca "Faltam X dias" e mantém a idade do lead como info secundária.
              <>
                <span style={{ ...badgeFollowupStyle(l.proximo_followup), padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', whiteSpace: 'nowrap' }}>{labelFaltam(ate)}</span>
                {dias != null && <span style={{ fontSize: '10px', color: '#9AA3AE', whiteSpace: 'nowrap' }}>• {labelCriado(dias)}</span>}
              </>
            ) : (
              // Sem follow-up futuro válido: mantém o comportamento atual (idade do lead).
              <span style={{ ...badgeFollowupStyle(l.proximo_followup), padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', whiteSpace: 'nowrap' }}>{labelDias(dias)}</span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#9AA3AE', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org.icon} {org.label}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {(perdido || fechado) && l.usuario_nome && (
              <span title={l.usuario_nome} style={{ width: '22px', height: '22px', borderRadius: '50%', background: sc.cor, color: '#fff', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{inicial(l.usuario_nome)}</span>
            )}
            <button onClick={e => { e.stopPropagation(); abrirDetalhe(l); }} title="Ação rápida"
              style={{ width: '24px', height: '24px', borderRadius: '50%', background: sc.cor, color: '#fff', border: 'none', cursor: 'pointer', fontSize: '15px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
          </div>
        </div>
      </div>
    );
  };

  const KanbanColuna = ({ s }) => {
    const lista = porStatus(s.v);
    if (colapsadas[s.v]) {
      return (
        <div onClick={() => toggleCol(s.v)} title={`Expandir ${s.l}`}
          onDragOver={e => { if (dragId == null) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverCol !== s.v) setDragOverCol(s.v); }}
          onDrop={e => { e.preventDefault(); const id = dragId != null ? dragId : Number(e.dataTransfer.getData('text/plain')); moverLead(id, s.v); setDragOverCol(null); setDragId(null); }}
          style={{ width: '46px', flexShrink: 0, background: dragOverCol === s.v ? '#E3EAF1' : '#F4F6F9', outline: dragOverCol === s.v ? `2px dashed ${s.cor}` : 'none', borderRadius: '10px', padding: '12px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: s.cor }} />
          <span style={{ background: s.cor, color: '#fff', fontSize: '10px', fontWeight: '700', borderRadius: '10px', padding: '1px 6px' }}>{lista.length}</span>
          <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: '12px', fontWeight: '700', color: s.cor }}>{s.l}</span>
        </div>
      );
    }
    return (
      <div
        onDragOver={e => { if (dragId == null) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverCol !== s.v) setDragOverCol(s.v); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(c => c === s.v ? null : c); }}
        onDrop={e => { e.preventDefault(); const id = dragId != null ? dragId : Number(e.dataTransfer.getData('text/plain')); moverLead(id, s.v); setDragOverCol(null); setDragId(null); }}
        style={{ width: '290px', flexShrink: 0, background: dragOverCol === s.v ? '#E3EAF1' : '#F4F6F9', outline: dragOverCol === s.v ? `2px dashed ${s.cor}` : 'none', outlineOffset: '-2px', borderRadius: '10px', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 230px)', transition: 'background 0.12s' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 10px 10px 14px' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.cor, flexShrink: 0 }} />
          {editandoCol === s.v ? (
            // Input NÃO-controlado (defaultValue, sem onChange): evita re-render do pai a cada tecla,
            // que remontaria este componente inline e roubaria o foco. Salva no blur; Enter confirma, Esc cancela.
            <input autoFocus defaultValue={s.l} maxLength={40}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                if (e.key === 'Escape') { cancelarEdicaoRef.current = true; e.target.blur(); }
              }}
              onBlur={e => {
                if (cancelarEdicaoRef.current) { cancelarEdicaoRef.current = false; setEditandoCol(null); return; }
                salvarLabel(s.v, e.target.value);
              }}
              style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: '700', color: '#1C2533', padding: '2px 6px', border: `1px solid ${s.cor}`, borderRadius: '6px', outline: 'none', fontFamily: "'DM Sans',system-ui,sans-serif" }} />
          ) : (
            <>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#1C2533', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.l}</span>
              {isAdmin && (
                <button onClick={() => setEditandoCol(s.v)} title="Renomear coluna"
                  style={{ ...iconBtn, width: '20px', height: '20px', fontSize: '11px', flexShrink: 0 }}>✎</button>
              )}
            </>
          )}
          <span style={{ background: '#fff', color: s.cor, border: `1px solid ${s.cor}33`, fontSize: '11px', fontWeight: '700', borderRadius: '20px', padding: '1px 8px', minWidth: '20px', textAlign: 'center' }}>{lista.length}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
            <button onClick={() => toggleCol(s.v)} title="Recolher coluna" style={iconBtn}>«</button>
            {!somenteLeitura && <button onClick={() => { setForm({ status: s.v }); setModalLead(true); }} title="Adicionar lead nesta etapa" style={iconBtn}>＋</button>}
          </div>
        </div>
        <div style={{ padding: '0 10px 12px', overflowY: 'auto', flex: 1 }}>
          {lista.length === 0
            ? <div style={{ fontSize: '12px', color: '#B5BEC9', textAlign: 'center', padding: '24px 0' }}>—</div>
            : lista.map(l => <CardLead key={l.id} l={l} />)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'DM Sans',system-ui,sans-serif" }}>
      {alertas.length > 0 && (
        <div style={{ background: '#C0392B', color: '#fff', padding: '10px 18px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '13px' }}>⚠ {alertas.length} follow-up(s) vencido(s)!</div>
            <div style={{ fontSize: '12px', opacity: 0.85 }}>{alertas.map(a => a.nome).join(', ')}</div>
          </div>
          <button onClick={tocarAlerta} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>🔔 Som</button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>CRM — Follow-up</h2>
        {!somenteLeitura && <button onClick={() => { setForm({}); setDupAviso(null); setModalLead(true); }}
          style={{ padding: '9px 18px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
          + Novo Lead
        </button>}
      </div>

      {/* BARRA SUPERIOR — Filtros / Busca / Período */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setMostrarFiltros(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: mostrarFiltros || filtroStatus ? '#1A6DFF' : '#fff', border: '0.5px solid rgba(26,58,92,0.2)', color: mostrarFiltros || filtroStatus ? '#fff' : '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
          ⚙ Filtros{filtroStatus ? ` · ${cfgM(filtroStatus).l}` : ''}
        </button>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder='🔍  Buscar lead, telefone ou veículo...'
          style={{ ...inpStyle, flex: '1 1 280px', maxWidth: '420px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#fff', border: '0.5px solid rgba(26,58,92,0.2)', borderRadius: '8px', color: '#6B7A8D', fontSize: '13px', fontWeight: '600' }}>
          📅 {periodoLabel()}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#9AA3AE', fontWeight: '600' }}>{totalVisivel} lead(s)</div>
      </div>

      {mostrarFiltros && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {[{ v: '', l: 'Todas as etapas' }, ...STATUS_M].map(s => (
            <button key={s.v} onClick={() => setFiltroStatus(s.v)}
              style={{ padding: '6px 12px', background: filtroStatus === s.v ? (s.cor || '#1A3A5C') : '#fff', border: '0.5px solid rgba(26,58,92,0.2)', color: filtroStatus === s.v ? '#fff' : '#6B7A8D', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: filtroStatus === s.v ? '700' : '500', fontFamily: "'DM Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>
              {s.l}
            </button>
          ))}
        </div>
      )}

      {/* BOARD KANBAN */}
      <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '10px', alignItems: 'flex-start' }}>
        {colunasVisiveis.map(s => <KanbanColuna key={s.v} s={s} />)}
      </div>

      {/* MODAL NOVO/EDITAR LEAD */}
      {modalLead && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1C2533' }}>{form.id ? 'Editar Lead' : 'Novo Lead'}</h3>
              <button onClick={() => { setModalLead(false); setForm({}); setDupAviso(null); }} style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#9AA3AE' }}>×</button>
            </div>
            {[['Nome', 'nome', 'text'], ['Telefone', 'telefone', 'text'], ['Interesse (modelo)', 'interesse', 'text']].map(([l, k, t]) => (
              <div key={k} style={{ marginBottom: '12px' }}>
                <label style={lbl}>{l}</label>
                <input type={t} value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={inpStyle} />
              </div>
            ))}
            <div style={{ marginBottom: '12px' }}>
              <label style={lbl}>Status</label>
              <select value={form.status || 'novo'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inpStyle}>
                {STATUS_M.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            {!form.id && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Próximo follow-up</label>
                  <input type='datetime-local' value={form.proximo_followup || ''} onChange={e => setForm(f => ({ ...f, proximo_followup: e.target.value }))} style={inpStyle} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Observação inicial</label>
                  <textarea value={form.observacao || ''} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))}
                    placeholder='Anote informações importantes sobre este lead...'
                    style={{ ...inpStyle, height: '65px', resize: 'vertical' }} />
                </div>
              </>
            )}
            {dupAviso && (
              <div style={{ marginBottom: '12px', padding: '12px 14px', background: '#FFF4E5', border: '1px solid #F0B429', borderRadius: '8px', fontSize: '13px', color: '#7A4B00' }}>
                <div style={{ fontWeight: 700, marginBottom: '6px' }}>⚠ Telefone já cadastrado</div>
                <div>
                  Este telefone já está cadastrado como <b>{dupAviso.nome || 'sem nome'}</b>
                  {dupAviso.data_cadastro && <> desde <b>{new Date(dupAviso.data_cadastro).toLocaleDateString('pt-BR')}</b></>}
                  {dupAviso.vendedor && <>, vendedor <b>{dupAviso.vendedor}</b></>}.
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button onClick={verCadastroExistente} style={{ flex: 1, padding: '8px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Ver cadastro</button>
                  <button onClick={() => salvarLead(true)} disabled={loading} style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid #F0B429', color: '#7A4B00', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cadastrar mesmo assim</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setModalLead(false); setForm({}); setDupAviso(null); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Cancelar</button>
              <button onClick={() => salvarLead()} disabled={loading} style={{ flex: 1, padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>{loading ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DETALHE */}
      {modalDetalhe && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>{modalDetalhe.nome}</div>
                <div style={{ fontSize: '12px', color: '#6B7A8D' }}>📱 {modalDetalhe.telefone} {modalDetalhe.interesse && `· 🏍️ ${modalDetalhe.interesse}`}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={() => abrirWhatsApp(modalDetalhe.telefone)} style={{ padding: '6px 12px', background: '#25D366', border: 'none', color: '#fff', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>💬 WhatsApp</button>
                {!somenteLeitura && <button onClick={() => { setForm(modalDetalhe); setModalDetalhe(null); setModalLead(true); }} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Editar</button>}
                <button onClick={() => setModalDetalhe(null)} style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9AA3AE' }}>×</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {STATUS_M.map(s => (
                <button key={s.v} disabled={somenteLeitura} onClick={async () => {
                  await api.put('/crm/leads/' + modalDetalhe.id, { ...modalDetalhe, status: s.v });
                  setModalDetalhe(d => ({ ...d, status: s.v }));
                  carregarLeads();
                }} style={{ padding: '4px 10px', background: modalDetalhe.status === s.v ? s.cor : s.bg, color: modalDetalhe.status === s.v ? '#fff' : s.cor, border: `1px solid ${s.cor}`, borderRadius: '20px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                  {s.l}
                </button>
              ))}
            </div>

            {modalDetalhe.proximo_followup && (
              <div style={{ background: isVencido(modalDetalhe.proximo_followup) ? '#FDECEA' : '#FFF3E0', border: `1px solid ${isVencido(modalDetalhe.proximo_followup) ? '#C0392B' : '#E67E22'}`, borderRadius: '7px', padding: '8px 12px', marginBottom: '16px', fontSize: '12px', color: isVencido(modalDetalhe.proximo_followup) ? '#C0392B' : '#B06000', fontWeight: '600' }}>
                {isVencido(modalDetalhe.proximo_followup) ? '⚠ Follow-up vencido: ' : '📅 Próximo follow-up: '}{fmt(modalDetalhe.proximo_followup)}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>Histórico</div>
              {historico.length === 0 && <div style={{ fontSize: '13px', color: '#9AA3AE' }}>Nenhuma interação registrada</div>}
              {historico.map(h => (
                <div key={h.id} style={{ borderLeft: '2px solid #E8ECF0', paddingLeft: '12px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9AA3AE', marginBottom: '2px' }}>
                    <span style={{ fontWeight: '600', color: '#1A3A5C' }}>{h.usuario_nome}</span>
                    <span>{fmt(h.created_at)}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#1C2533' }}>{h.descricao}</div>
                  {h.proximo_followup && (
                    <div style={{ fontSize: '11px', color: '#E67E22', fontWeight: '600', marginTop: '3px' }}>📅 Agendado: {fmt(h.proximo_followup)}</div>
                  )}
                </div>
              ))}
            </div>

            {!somenteLeitura && <div style={{ borderTop: '0.5px solid rgba(26,58,92,0.1)', paddingTop: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '10px' }}>Registrar interação</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={lbl}>Tipo</label>
                  <select value={formHist.tipo} onChange={e => setFormHist(f => ({ ...f, tipo: e.target.value }))} style={inpStyle}>
                    <option value='observacao'>Observação</option>
                    <option value='ligacao'>Ligação</option>
                    <option value='whatsapp'>WhatsApp</option>
                    <option value='visita'>Visita</option>
                    <option value='proposta'>Proposta enviada</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Próximo follow-up</label>
                  <input type='datetime-local' value={formHist.proximo_followup} onChange={e => setFormHist(f => ({ ...f, proximo_followup: e.target.value }))} style={inpStyle} />
                </div>
              </div>
              {formHist.proximo_followup && (
                <div style={{ background: '#FFF3E0', border: '1px solid #E67E22', borderRadius: '6px', padding: '6px 10px', marginBottom: '8px', fontSize: '12px', color: '#B06000', fontWeight: '600' }}>
                  📅 Agendando follow-up para: {new Date(formHist.proximo_followup).toLocaleString('pt-BR', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                </div>
              )}
              <div style={{ marginBottom: '8px' }}>
                <label style={lbl}>Alterar status</label>
                <select value={formHist.status || ''} onChange={e => setFormHist(f => ({ ...f, status: e.target.value }))} style={inpStyle}>
                  <option value=''>Manter status atual</option>
                  {STATUS_M.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </div>
              <textarea value={formHist.descricao} onChange={e => setFormHist(f => ({ ...f, descricao: e.target.value }))}
                placeholder='Descreva o contato realizado...'
                style={{ ...inpStyle, height: '70px', resize: 'vertical', marginBottom: '8px' }} />
              <button onClick={salvarHistorico}
                style={{ width: '100%', padding: '10px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                Registrar interação
              </button>
            </div>}
          </div>
        </div>
      )}
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.7} }`}</style>
    </div>
  );
}
